/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { SearchMedium } from '@zoopla-eng/zds-components/icons';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import triggerTracking from '@blueprint/tracking';
import type { LocationType } from 'shared/components/SimpleLocationSearchInput';
import { SimpleLocationSearchInput } from 'shared/components/SimpleLocationSearchInput';
import { Search } from './HousePricesSearch.styles';
import HeroShort from './HeroShort';
import HeroLong from './HeroLong';

export const buildSearchUrl = (identifier: string, value: string) => {
    const housePricesPath = '/house-prices/';
    const q = `?q=${encodeURIComponent(value)}`;
    const url = `${housePricesPath}${identifier}${
        identifier ? '/' : ''
    }${q}&search_source=house-prices`;

    return url;
};

export const trackEvent = (isLandingPage: boolean) => {
    triggerTracking({
        event: 'GAEvent',
        eventAction: 'click',
        eventCategory: 'search results',
        eventLabel: isLandingPage ? 'house prices' : 'house prices results'
    });
};

type Props = {
    isNotAutoInitialized?: boolean;
    isLandingPage?: boolean;
};

export const HousePricesSearch: FC<Props> = ({
    isNotAutoInitialized = true,
    isLandingPage = false
}) => {
    const router = useRouter();
    const [q, setQ] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [locationText, setLocationText] = useState('');

    const value = locationText || q;

    const searchSubmit = () => {
        const searchUrl = buildSearchUrl(identifier, value);

        triggerTracking({
            event: 'GAEvent',
            eventAction: 'click',
            eventCategory: 'search results',
            eventLabel: isLandingPage ? 'house prices' : 'house prices results'
        });

        router.push(searchUrl);
    };

    const onInputTextChange = (value: string) => {
        setLocationText(value);
        setIdentifier('');
    };

    const onLocationSelect = (location: LocationType) => {
        setQ(location.value as string);
        setIdentifier(location.id);
        setLocationText('');
    };

    useEffect(() => {
        if (!isNotAutoInitialized) {
            setQ(router.query.q as string);
            setIdentifier((router.query['search-path'] as string[]).join('/'));
        }
    }, [router.asPath, router.query]);

    const input = (
        <Search>
            <SimpleLocationSearchInput
                id="location-search"
                searchSubmit={searchSubmit}
                onLocationSelect={onLocationSelect}
                onInputTextChange={onInputTextChange}
                value={q}
                searchType="house-prices"
            />
            <ButtonLeft
                type="primary"
                icon={<SearchMedium />}
                onClick={searchSubmit}
                disabled={!value}
                data-testid="search-btn"
                color="$textContrast"
            >
                Search
            </ButtonLeft>
        </Search>
    );

    return isLandingPage ? (
        <HeroShort>{input}</HeroShort>
    ) : (
        <HeroLong>{input}</HeroLong>
    );
};
